/**
 * 动态适配下拉框
 */

import {
  AdvancedSelect,
  SelectValue,
  SelectOptionValue,
  ISelectOptionItem,
} from '@tencent/udesign/dist';
import {
  getAllowClearConfig,
  getDynamicSelectedValue,
  getDynamicSelectOption,
  getPlaceholderConfig,
  getSelectTypeConfig,
  getTableBodyLinkageValue,
  isSelectComponent,
  getCheckItem,
  searchOptions,
  AUTO_TABLE_CUSTOM_VALUE_ALL,
} from '../../../libs';
import {
  FieldType,
  IFieldValueExtensionSelect,
  IFieldTypeOptBySelect,
  IPropsSelect,
} from '../../../model';
import { useEffect, useState, useRef } from 'react';
import { ForwardSelectedLabel } from './render-selected-label';
import { cloneDeep, isEqual } from 'lodash';
// select 下拉框 热度排序

export const DynamicAdvancedSelect = <T,>(props: IPropsSelect<T>) => {
  const { isFilterModeView, fieldConfig, selectedValue } = props;
  const filterOptExtension = isSelectComponent(fieldConfig.filterOptExtension);
  const isMultiple =
    fieldConfig.filter_type === FieldType.advanced_select_multiple;
  const selectAllLabel =
    (filterOptExtension as IFieldTypeOptBySelect).selectAllLabel || '全选';
  const selectedLabelRef = useRef<any | null>(null);
  const [options, setOptions] = useState<Array<IFieldValueExtensionSelect>>([]);
  const [newOptions, setNewOptions] = useState<
    Array<IFieldValueExtensionSelect>
  >([]);
  const [selectAllValue, setSelectAllValue] = useState<
    ReadonlyArray<SelectOptionValue>
  >([]);
  const [openSelect, setOpenSelect] = useState<boolean>(false);

  const sortArr = (
    options: Array<ISelectOptionItem>,
    currentSelect?: SelectValue
  ) => {
    const newSelectedValue = currentSelect || selectedValue.value;
    const orderedOptions = cloneDeep(options);
    orderedOptions.sort((a, b) => {
      if (
        Array.isArray(newSelectedValue) &&
        newSelectedValue.includes(a.value)
      ) {
        return -1;
      }
      if (
        Array.isArray(newSelectedValue) &&
        newSelectedValue.includes(b.value)
      ) {
        return 1;
      }
      return 0;
    });

    // console.log(orderedOptions);

    orderedOptions.sort((a) => {
      if (a.value === AUTO_TABLE_CUSTOM_VALUE_ALL) {
        return -1;
      }
      return 1;
    });
    return orderedOptions;
  };

  useEffect(() => {
    if (isFilterModeView && filterOptExtension) {
      const isSelectAll = isMultiple && filterOptExtension.selectAll;
      const options = getDynamicSelectOption(
        selectedValue.value,
        fieldConfig?.fieldOptExtension?.data || props.options,
        isSelectAll,
        selectAllLabel
      );

      setOptions(options);
      const newOpt = searchOptions(
        selectedLabelRef?.current?.getKeyword() || '',
        options
      );

      const old = newOptions.map((v) => v.value);
      old.sort();
      const latest = newOpt.map((v) => v.value);
      latest.sort();
      if (!isEqual(latest, old)) {
        setNewOptions(newOpt);
      }

      // 获取全选列表
      if (isSelectAll && options.length > 0) {
        setSelectAllValue(options.map((item) => item.value));
      }
    }
  }, [
    isFilterModeView,
    filterOptExtension,
    fieldConfig?.fieldOptExtension?.data,
    props.options,
  ]);

  // Filters
  if (isFilterModeView) {
    if (!filterOptExtension) {
      return null;
    }

    const type = getSelectTypeConfig(filterOptExtension);
    const isSelectAll = isMultiple && filterOptExtension.selectAll; // 当 筛选框 为多选 而且 使用全选功能时
    const placeholder = getPlaceholderConfig(fieldConfig);
    const newValue = getDynamicSelectedValue(selectedValue, isMultiple);

    const getCurrentValue = (value: SelectValue) => {
      if (Array.isArray(value) && Array.isArray(newValue)) {
        let currentSelect = value.slice();
        if (!newValue.length) return currentSelect;
        const newOptValues = newOptions.map((v) => v.value);
        for (const it of newValue) {
          if (!value.includes(it as unknown as never)) {
            if (!newOptValues.includes(it)) {
              currentSelect = [it, ...currentSelect];
            }
          }
        }
        return currentSelect;
      }
      return [];
    };

    const onChanged = (value: SelectValue) => {
      const clearSearchKeywords =
        typeof filterOptExtension?.clearSearchKeywords === 'boolean'
          ? !!filterOptExtension?.clearSearchKeywords
          : true;

      // 当使用 全选功能 时 在选中项的回调中清空子组件的input参数
      if (selectedLabelRef?.current && isSelectAll && clearSearchKeywords) {
        selectedLabelRef.current.emptyInput();
      }

      // 确定现在选择的所有值
      const currentSelect = isMultiple ? getCurrentValue(value) : value;

      // 当启用全选功能时
      if (isSelectAll && Array.isArray(currentSelect)) {
        const checkItem = getCheckItem(currentSelect, newValue, selectAllValue);
        return props.onChanged(fieldConfig.key, checkItem);
      }

      props.onChanged(fieldConfig.key, currentSelect);
    };

    const onClickClear = () => {
      onChanged([]);
    };

    const onFocus = () => {
      // 当使用 全选功能 才 使用 手动聚焦input框
      if (selectedLabelRef?.current && isSelectAll) {
        selectedLabelRef.current.focusInput();
      }
    };

    // 根据子组件 input 值过滤筛选参数
    const selectValue = (keyWord: string) => {
      const list = searchOptions(keyWord, options);
      setNewOptions(sortArr(list, selectedValue.value));
    };

    // 在select关闭筛选框时 改变options
    const onChangeOrderedOptions = () => {
      let orderedOptions = sortArr(
        options.filter((item) => item.value !== AUTO_TABLE_CUSTOM_VALUE_ALL),
        selectedValue.value
      );
      // 热度排序时 将全选放着第一位
      if (isSelectAll) {
        orderedOptions = [
          {
            label: selectAllLabel,
            value: AUTO_TABLE_CUSTOM_VALUE_ALL,
          },
          ...orderedOptions,
        ];
      }
      setNewOptions(orderedOptions);
    };

    const renderLabel = () => {
      return (
        <ForwardSelectedLabel
          type={type}
          placeholder={placeholder}
          isMultiple={isMultiple}
          selectAllLabel={selectAllLabel}
          fieldConfig={fieldConfig}
          onClickClear={onClickClear}
          newValue={newValue}
          isSelectAll={isSelectAll}
          openSelect={openSelect}
          setOpenSelect={setOpenSelect}
          selectValue={selectValue}
          ref={selectedLabelRef}
        />
      );
    };
    return (
      <AdvancedSelect
        type={type}
        placeholder={placeholder}
        value={newValue}
        onChanged={onChanged}
        options={newOptions as Array<IFieldValueExtensionSelect>}
        mode={isMultiple ? 'multiple' : undefined}
        allowClear={getAllowClearConfig(filterOptExtension)}
        className={props.className}
        renderSelectedLabel={isSelectAll ? renderLabel : undefined}
        onOptionsOrderChange={onChangeOrderedOptions}
        onFocus={onFocus}
        open={isSelectAll ? openSelect : undefined}
        {...filterOptExtension}
        dropDownProps={{
          container: () =>
            document.getElementsByClassName('fullscreen-enabled')[0] ||
            document.body,
        }}
        extendedConfig={{
          supportSelectedTop: true,
          supportFreqSort: true,
          ignoreCase: true,
        }}
      />
    );
  }

  const bodyValue = getTableBodyLinkageValue(selectedValue, fieldConfig);
  if (bodyValue) {
    return <span>{bodyValue}</span>;
  }
  return <span>{selectedValue.value}</span>;
};
